import React, { useState } from "react";
import { Calculator, ArrowRight, TrendingUp } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const fmt = (n) => `R$ ${Math.round(n).toLocaleString("pt-BR")}`;

export default function RoiCalculator() {
  const [revenue, setRevenue] = useState(150000); // R$/mês
  const [products, setProducts] = useState(500);
  const [rupture, setRupture] = useState(10); // %
  const [idle, setIdle] = useState(15); // %

  // Estimated savings:
  // Reduction of rupture from X% to ~4% recovers (X-4)/100 * 0.7 (assumed lost-sale recovery factor) of revenue
  const ruptureReductionPct = Math.max(0, rupture - 4);
  const monthlySavings = revenue * (ruptureReductionPct / 100) * 0.7;
  const annualSavings = monthlySavings * 12;

  // Idle stock reduction: assume 60% of idle products * avg cost
  const avgInventoryValue = revenue * 1.2; // rough proxy
  const idleReductionPct = Math.max(0, idle - 5);
  const idleCapitalFreed = avgInventoryValue * (idleReductionPct / 100) * 0.6;

  const total12mo = annualSavings + idleCapitalFreed;

  return (
    <div className="rounded-2xl border border-primary/30 bg-card overflow-hidden glow-coral">
      <div className="grid lg:grid-cols-2 gap-0">
        {/* Inputs */}
        <div className="p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-border">
          <div className="flex items-center gap-2 mb-2">
            <Calculator className="w-4 h-4 text-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-primary">Calculadora</span>
          </div>
          <h3 className="font-display text-3xl font-bold mb-6 tracking-tight">
            Quanto você economizaria com SmartStockAI?
          </h3>
          <p className="text-sm text-muted-foreground mb-8">
            Ajuste os valores abaixo conforme sua operação. A estimativa é conservadora, baseada na média de redução observada em clientes ativos.
          </p>

          <div className="space-y-7">
            <Field label="Faturamento mensal" value={fmt(revenue)}>
              <Slider data-testid="roi-revenue" min={20000} max={2000000} step={10000} value={[revenue]} onValueChange={(v) => setRevenue(v[0])} />
            </Field>
            <Field label="Quantidade de produtos (SKUs)" value={products.toLocaleString("pt-BR")}>
              <Slider data-testid="roi-products" min={20} max={5000} step={20} value={[products]} onValueChange={(v) => setProducts(v[0])} />
            </Field>
            <Field label="Taxa atual de ruptura" value={`${rupture}%`}>
              <Slider data-testid="roi-rupture" min={2} max={30} step={1} value={[rupture]} onValueChange={(v) => setRupture(v[0])} />
            </Field>
            <Field label="Estoque parado (% do total)" value={`${idle}%`}>
              <Slider data-testid="roi-idle" min={2} max={40} step={1} value={[idle]} onValueChange={(v) => setIdle(v[0])} />
            </Field>
          </div>
        </div>

        {/* Result */}
        <div className="p-8 lg:p-10 bg-gradient-to-br from-primary/10 to-transparent flex flex-col justify-center">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground mb-3">
            <TrendingUp className="w-3.5 h-3.5 text-primary" /> Estimativa de economia
          </div>
          <div className="font-display text-5xl sm:text-6xl font-bold text-primary leading-none mb-2" data-testid="roi-total">
            {fmt(total12mo)}
          </div>
          <div className="text-sm text-muted-foreground mb-8">em 12 meses · Baseado nos seus dados</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ResultCard label="Recuperação de ruptura" value={fmt(annualSavings)} hint="receita não perdida/ano" />
            <ResultCard label="Capital de giro liberado" value={fmt(idleCapitalFreed)} hint="estoque parado convertido" />
          </div>

          <Link to="/signup" className="mt-8 block">
            <Button data-testid="roi-cta-btn" size="lg" className="w-full bg-primary hover:bg-primary/90 h-12">
              Quero começar grátis <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <div className="text-[11px] text-muted-foreground mt-3 text-center">
            Estimativa baseada em redução média observada (ruptura → 4%, parado → 5%, recuperação 70% das vendas perdidas).
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-3">
      <span className="text-sm font-medium">{label}</span>
      <span className="font-mono text-primary text-sm">{value}</span>
    </div>
    {children}
  </div>
);

const ResultCard = ({ label, value, hint }) => (
  <div className="p-4 rounded-xl border border-border bg-card">
    <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="font-display text-2xl font-bold mt-2">{value}</div>
    <div className="text-xs text-muted-foreground mt-1">{hint}</div>
  </div>
);
